import { useState, useRef, useCallback, memo } from 'react';

import { Modal } from '@stratumn/atomic';

import { TRAY_PORTAL_RIGHT } from 'constant/htmlIds';
import { CodeViewer } from '@/components/ui/utils/code/codeViewer';
import { Button } from '@/shadcn/button';
import { Editor } from 'codemirror';
import { notify } from '@/components/toast';
import { cn } from '@/shadcn/lib/utils';
import { Tray } from '@/components/ui/tray/tray';

type Props = {
  inModal?: boolean;
  title?: string;
  jsonString: string;
  onSubmit?: (jsonString: string | undefined) => Promise<void>;
  onClose: () => void;
  codemirrorOptions?: Record<string, unknown>;
  submitButtonLabel?: string;
  closeAfterSubmit?: boolean;
};

export const JsonEditor = memo(
  ({
    inModal = false,
    title = 'Edit JSON string',
    jsonString,
    onSubmit,
    onClose,
    codemirrorOptions,
    submitButtonLabel = 'Save',
    closeAfterSubmit = false
  }: Props) => {
    const [parsingErr, setParsingErr] = useState<string | undefined>(undefined);
    const editorRef = useRef<Editor | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
      if (!onSubmit) return;
      const currentStr = editorRef.current?.getValue();

      try {
        if (currentStr) JSON.parse(currentStr);
        setParsingErr(undefined);
      } catch (e) {
        const errorStr = `Error parsing object:\n${e instanceof Error ? e.toString() : 'Unknown error'}`;
        notify.error(errorStr);
        setParsingErr(errorStr);
        console.error(errorStr);
        return;
      }

      setSubmitting(true);
      await onSubmit(currentStr);
      setSubmitting(false);
      if (closeAfterSubmit) {
        onClose();
      }
    };

    const refreshEditor = useCallback(() => editorRef.current?.refresh(), []);

    const editorComponent = (
      <div
        className={cn(
          'flex h-full flex-col flex-nowrap',
          inModal && 'overflow-auto'
        )}
      >
        <div
          className={cn(
            'h-full overflow-hidden text-sm/tight',
            inModal && 'overflow-auto p-3.5'
          )}
          data-cy="editor-body"
        >
          <CodeViewer
            codeString={jsonString}
            codemirrorOptions={codemirrorOptions}
            readOnly={false}
            setEditorRef={editor => {
              editorRef.current = editor;
            }}
          />
        </div>
        <div className="flex h-[70px] w-full flex-row flex-nowrap items-center justify-between p-4">
          {parsingErr && (
            <div
              className="text-destructive truncate text-base"
              title={parsingErr}
              data-testid="json-editor-error-message"
            >
              Invalid JSON string! {parsingErr}
            </div>
          )}
          <div className="flex w-full flex-row flex-nowrap items-center justify-end gap-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={false}
              data-testid="json-editor-close-btn"
              data-cy="json-editor-close-btn"
              aria-label="Close the editor"
            >
              CLOSE
            </Button>
            {onSubmit && (
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="json-editor-submit-btn"
                data-cy="json-editor-submit-btn"
                aria-label="Submit the new value"
              >
                {submitButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    );

    return inModal ? (
      <Modal title={title} handleCollapse={onClose} large>
        {editorComponent}
      </Modal>
    ) : (
      <Tray
        portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
        title={title}
        width={600}
        onResize={refreshEditor}
        onClose={onClose}
      >
        {editorComponent}
      </Tray>
    );
  }
);
